/**
 * Step 1 › which InMail template this role uses and after how many days the follow-up goes
 * (Sofia, Oct 22 2026). "Automatic" = the tech InMail for a tech role, else your own template,
 * else the company standard — the same order the proxy resolves (`lib/outreachTemplates.js`).
 * Native `<select>`/`<input>`, like the language select next to them.
 */
import { useEffect, useState } from 'react';
import { fetchEmailTemplates, type EmailTemplate } from '../../services/emailTemplatesApi';
import type { BriefDoc } from '../../types/taOutreach';

const AUTO = 'auto';
const FIELD = 'h-8 rounded-md border border-solid border-[#E9EAEC] bg-white px-2';

export function InMailSettings({
  brief,
  onSave,
}: {
  brief: BriefDoc;
  onSave: (patch: { inmailTemplate?: { scope: 'company' | 'personal'; key: string } | null; followUpAfterDays?: number }) => void | Promise<void>;
}) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const saved = brief.followUpAfterDays ?? 5;
  const [days, setDays] = useState(String(saved));
  // A save (or another tab's) moves the stored number: the field follows it (React's
  // "adjust state when a prop changes" pattern, not an effect).
  const [seen, setSeen] = useState(saved);
  if (seen !== saved) {
    setSeen(saved);
    setDays(String(saved));
  }

  useEffect(() => {
    let alive = true;
    fetchEmailTemplates()
      .then((all) => alive && setTemplates(all.filter((t) => t.category === 'outreach-inmail' && t.active !== false)))
      .catch(() => alive && setTemplates([]));
    return () => {
      alive = false;
    };
  }, []);

  const pick = brief.inmailTemplate ? `${brief.inmailTemplate.scope}:${brief.inmailTemplate.key}` : AUTO;
  const tech = brief.tech ?? brief.language === 'en';
  const commitDays = () => {
    const n = Number(days);
    if (Number.isInteger(n) && n >= 1 && n <= 30 && n !== saved) void onSave({ followUpAfterDays: n });
    else setDays(String(saved));
  };

  return (
    <>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-[#707070]">InMail template</span>
        <select
          className={FIELD}
          value={pick}
          onChange={(e) => {
            const [scope, key] = e.target.value.split(':');
            void onSave({ inmailTemplate: e.target.value === AUTO ? null : { scope: scope as 'company' | 'personal', key } });
          }}
        >
          <option value={AUTO}>Automatic ({tech ? 'tech InMail — tech role' : 'yours, else the company standard'})</option>
          {templates.map((t) => (
            <option key={t.id} value={`${t.scope}:${t.key}`}>
              {t.scope === 'company' ? 'Company' : 'Mine'}: {t.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-[#707070]">Follow-up after (days)</span>
        <input
          type="number"
          min={1}
          max={30}
          className={`${FIELD} w-24`}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          onBlur={commitDays}
          onKeyDown={(e) => e.key === 'Enter' && commitDays()}
        />
      </label>
    </>
  );
}
